import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import {
  cancelClientAdvance,
  deleteClientAdvance,
  getClientAdvances,
  receiveClientAdvance,
  refundClientAdvance,
} from "features/ClientAdvances/services/ClientAdvanceService";
import DeleteModal from "components/common/DeleteModal";
import { ClientAdvance } from "features/ClientAdvances/types/client-advance.types";
import { toast } from "react-toastify";
import { ColumnDef } from "@tanstack/react-table";
import StatusBadge from "components/common/StatusBadge";
import {
  ClientAdvanceSourceTypeLabel,
  ClientAdvanceStatus,
  clientAdvanceStatusMap,
} from "features/ClientAdvances/constants/client-advance.constants";
import {
  CodeCell,
  IndexColumn,
  SourceColumn,
  resolveMappedSourceCell,
} from "components/common/columns";
import { DateTime } from "components/common/Date";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import CancelModal from "../CancelModal";
import ReceiveModal from "../ReceiveModal";
import RefundModal from "../RefundModal";
import LivePreviewBadge from "../components/LivePreviewBadge";
import { formatCurrency } from "utils/currency";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import ClientAdvanceListFilters from "./ClientAdvanceListFilters";

export const ClientAdvancesTable = () => {
  const { can } = useAccess();
  const canReadAdv = can(Permissions.CLIENT_ADVANCE.READ);
  const canUpdateAdv = can(Permissions.CLIENT_ADVANCE.UPDATE);
  const canDeleteAdv = can(Permissions.CLIENT_ADVANCE.DELETE);
  const canCancelAdv = can(Permissions.CLIENT_ADVANCE.CANCEL);
  const canReceiveAdv = can(Permissions.CLIENT_ADVANCE.RECEIVE);
  const canRefundAdv = can(Permissions.CLIENT_ADVANCE.REFUND);
  const tableRef = useRef<() => void>(null);
  const [advance, setAdvance] = useState<ClientAdvance | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState("");
  const [receiveModal, setReceiveModal] = useState<boolean>(false);
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);
  const [refundModal, setRefundModal] = useState<boolean>(false);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [sourceType, setSourceType] = useState("");

  const requestParams = useMemo(
    () => ({ status: status || undefined, sourceType: sourceType || undefined }),
    [status, sourceType],
  );

  const filterCount = [status, sourceType].filter(Boolean).length;

  const onClickDelete = useCallback((record: ClientAdvance) => {
    setAdvance(record);
    setDeleteModal(true);
  }, []);

  const onClickCancel = useCallback((record: ClientAdvance) => {
    setAdvance(record);
    setCancelReason("");
    setCancelModal(true);
  }, []);

  const onClickReceive = useCallback((record: ClientAdvance) => {
    setAdvance(record);
    setReceiveModal(true);
  }, []);

  const onClickRefund = useCallback((record: ClientAdvance) => {
    setAdvance(record);
    setRefundModal(true);
  }, []);

  const handleDelete = async () => {
    if (advance) {
      try {
        const response = await deleteClientAdvance(Number(advance?.id));
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setDeleteModal(false);
      } catch (err) {
        toast.error((err as any).response?.data?.message ?? "Could not delete advance.");
      }
    }
  };

  const handleCancel = async () => {
    if (advance) {
      try {
        const response = await cancelClientAdvance(advance.id, cancelReason);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setCancelModal(false);
      } catch (err) {
        toast.error((err as any).response?.data?.message ?? "Could not cancel advance.");
      }
    }
  };

  const handleReceive = async (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => {
    if (!advance) return;
    setReceiveSubmitting(true);
    try {
      const response = await receiveClientAdvance(advance.id, payload);
      if (response.success) {
        toast.success(response.message);
        tableRef.current?.();
        setReceiveModal(false);
      } else {
        toast.error(response.message ?? "Could not receive advance.");
      }
    } catch (err) {
      toast.error((err as any).response?.data?.message ?? "Could not receive advance.");
    } finally {
      setReceiveSubmitting(false);
    }
  };

  const handleRefund = async (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => {
    if (!advance) return;
    setRefundSubmitting(true);
    try {
      const response = await refundClientAdvance(advance.id, payload);
      if (response.success) {
        toast.success(response.message);
        tableRef.current?.();
        setRefundModal(false);
      } else {
        toast.error(response.message ?? "Could not refund advance.");
      }
    } catch (err) {
      toast.error((err as any).response?.data?.message ?? "Could not refund advance.");
    } finally {
      setRefundSubmitting(false);
    }
  };

  const columns: ColumnDef<ClientAdvance>[] = useMemo(
    () => [
      IndexColumn<ClientAdvance>(),
      {
        header: "Code",
        cell: ({ row }) => {
          const r = row.original;
          return canReadAdv ? (
            <Link to={`/finance/client-advances/${r.id}`} className="text-decoration-none link">
              <CodeCell code={r.code} />
            </Link>
          ) : (
            <CodeCell code={r.code} />
          );
        },
        accessorKey: "code",
        enableColumnFilter: false,
      },
      SourceColumn<ClientAdvance>({
        resolve: (row) =>
          resolveMappedSourceCell(row.sourceType, row.sourceId, {
            typeLabelMap: ClientAdvanceSourceTypeLabel,
            source: row.source,
          }),
      }),
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <>
            {formatCurrency(row.original.amount)}
            {row.original.isLivePreview ? <LivePreviewBadge /> : null}
          </>
        ),
      },
      {
        header: "Settled",
        accessorKey: "settledAmount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => formatCurrency(row.original.settledAmount ?? 0),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <StatusBadge value={row.original.status} map={clientAdvanceStatusMap} />
        ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => <DateTime date={row.original.created_at} />,
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const isDraft = r.status === ClientAdvanceStatus.DRAFT;
          const showEdit = canUpdateAdv && isDraft;
          const showCancel = canCancelAdv && isDraft;
          const showReceive = canReceiveAdv && isDraft;
          const showRefund =
            canRefundAdv &&
            r.status === ClientAdvanceStatus.PROCESSED &&
            (r.settleableAmount ?? 0) > 0;
          const showDelete =
            canDeleteAdv &&
            (r.status === ClientAdvanceStatus.DRAFT ||
              r.status === ClientAdvanceStatus.CANCELLED);
          const hasMenuItems =
            canReadAdv ||
            showEdit ||
            showCancel ||
            showReceive ||
            showRefund ||
            showDelete;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }

          const hasItemsBeforeDelete =
            canReadAdv || showEdit || showCancel || showReceive || showRefund;

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Client advance actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canReadAdv ? (
                  <DropdownItem tag={Link} to={`/finance/client-advances/${r.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {showEdit ? (
                  <DropdownItem tag={Link} to={`/finance/client-advances/edit/${r.id}`}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {showReceive ? (
                  <DropdownItem onClick={() => onClickReceive(r)}>
                    <i className="ri-hand-coin-line align-bottom me-2 text-success" />
                    Receive
                  </DropdownItem>
                ) : null}
                {showRefund ? (
                  <DropdownItem onClick={() => onClickRefund(r)}>
                    <i className="ri-arrow-go-back-line align-bottom me-2 text-danger" />
                    Refund
                  </DropdownItem>
                ) : null}
                {showCancel ? (
                  <DropdownItem onClick={() => onClickCancel(r)}>
                    <i className="ri-close-circle-line align-bottom me-2 text-muted" />
                    Cancel
                  </DropdownItem>
                ) : null}
                {showDelete ? (
                  <>
                    {hasItemsBeforeDelete ? <DropdownItem divider /> : null}
                    <DropdownItem className="text-danger" onClick={() => onClickDelete(r)}>
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      onClickDelete,
      onClickCancel,
      onClickReceive,
      onClickRefund,
      canReadAdv,
      canUpdateAdv,
      canDeleteAdv,
      canCancelAdv,
      canReceiveAdv,
      canRefundAdv,
    ],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <CancelModal
        show={cancelModal}
        reason={cancelReason}
        onReasonChange={setCancelReason}
        onCancelClick={handleCancel}
        onCloseClick={() => {
          setCancelModal(false);
          setCancelReason("");
        }}
        record={advance}
      />

      <ReceiveModal
        show={receiveModal}
        record={advance}
        submitting={receiveSubmitting}
        onCloseClick={() => setReceiveModal(false)}
        onConfirm={(payload) => void handleReceive(payload)}
      />

      <RefundModal
        show={refundModal}
        record={advance}
        submitting={refundSubmitting}
        onCloseClick={() => setRefundModal(false)}
        onConfirm={(payload) => void handleRefund(payload)}
      />

      <TableContainer
        columns={columns}
        fetchApi={getClientAdvances}
        requestParams={requestParams}
        pageSize={10}
        globalSearch={true}
        refreshRef={tableRef}
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
        filterCount={filterCount}
        onFiltersClear={() => {
          setStatus("");
          setSourceType("");
        }}
        toolbarRight={
          <ClientAdvanceListFilters
            status={status}
            onStatusChange={setStatus}
            sourceType={sourceType}
            onSourceTypeChange={setSourceType}
          />
        }
      />
    </React.Fragment>
  );
};
